import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Inbox, Loader2, CheckCircle2, X, FileText, Zap } from "lucide-react";

/**
 * Gmail statement auto-fetch UI. Sits above the statements table.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Gmail sync                                                   │
 *   │ Not connected → [Connect Gmail]                              │
 *   │ Connected as x@y.com → [Sync last 45 days]  N pending review │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Clicking "N pending review" opens a modal that shows every PDF the
 * sync engine downloaded — finance eyeballs each, tweaks the statement
 * month, then clicks Accept to route the PDF through the existing OCR
 * pipeline. Rejecting cleans the file up.
 */
export default function GmailSyncPanel({ onSynced }) {
    const [status, setStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [pending, setPending] = useState([]);
    const [pendingOpen, setPendingOpen] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const loadStatus = useCallback(async () => {
        try {
            const [s, p] = await Promise.all([
                api.get("/oauth/gmail/status"),
                api.get("/gmail/pending"),
            ]);
            setStatus(s.data);
            setPending(p.data.rows || []);
        } catch {
            setStatus({ configured: false, connected: false });
        }
    }, []);

    useEffect(() => {
        loadStatus();
        // Notice the callback landing on /statements?gmail=connected|denied|error
        const params = new URLSearchParams(window.location.search);
        const g = params.get("gmail");
        if (g === "connected") toast.success("Gmail connected. Click 'Sync from Gmail' to fetch statements.");
        if (g === "denied") {
            const reason = params.get("reason");
            toast.error("Gmail access denied", { description: reason ? decodeURIComponent(reason) : "Nothing was connected." });
        }
        if (g === "error") {
            const reason = params.get("reason");
            toast.error("Gmail connection failed", { description: reason ? decodeURIComponent(reason) : "Please try again." });
        }
        if (g) {
            // Strip the params from the URL so a refresh doesn't retoast
            const url = new URL(window.location.href);
            url.searchParams.delete("gmail");
            url.searchParams.delete("reason");
            window.history.replaceState({}, "", url.toString());
        }
    }, [loadStatus]);

    const connect = () => {
        // Send the browser to the OAuth start endpoint — it 302s to Google.
        // Use a relative path so the redirect works across preview,
        // production, and any custom domain (avoids hardcoded origin).
        window.location.href = "/api/oauth/gmail/login";
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect Gmail? You'll need to re-authorize to sync again.")) return;
        await api.post("/oauth/gmail/disconnect");
        toast.success("Gmail disconnected");
        loadStatus();
    };

    const sync = async () => {
        setSyncing(true);
        setLastResult(null);
        try {
            const res = await api.post("/gmail/sync", { days: 45 });
            setLastResult(res.data);
            if (res.data.new_pending_statements > 0) {
                toast.success(`Fetched ${res.data.new_pending_statements} new statement(s). Review below.`);
            } else if (res.data.pdfs_found > 0) {
                toast.message("No new statements", { description: "All PDFs already fetched or already saved." });
            } else {
                toast.message("Sync complete", {
                    description: "No matching emails found in the last 45 days. Check the 'Statement email from' and 'Subject' fields on your cards.",
                });
            }
            loadStatus();
            onSynced?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSyncing(false);
        }
    };

    if (!status) return null;

    if (!status.configured) {
        return (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4" data-testid="gmail-panel-unconfigured">
                <div className="flex items-start gap-3">
                    <Mail size={16} className="text-amber-700 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-medium text-amber-900">Gmail auto-fetch is not configured yet</div>
                        <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                            To fetch statements automatically from your inbox, an admin needs to add a Google
                            Cloud OAuth client to the server (env vars <code className="bg-amber-100 px-1 rounded">GMAIL_OAUTH_CLIENT_ID</code> and{" "}
                            <code className="bg-amber-100 px-1 rounded">GMAIL_OAUTH_CLIENT_SECRET</code>). See Help → Gmail sync.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="rounded-xl border border-stone-200 bg-white p-4 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="gmail-sync-panel">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${status.connected ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                        <Mail size={16} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-stone-900">Gmail auto-fetch</div>
                        <div className="text-[11px] text-stone-500 truncate">
                            {status.connected
                                ? <>Connected · <span className="text-stone-800">{status.email}</span></>
                                : "Pull statements straight from your inbox — no manual uploads."}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {pending.length > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPendingOpen(true)}
                            className="border-amber-300 text-amber-800 hover:bg-amber-50"
                            data-testid="gmail-pending-open"
                        >
                            <Inbox size={13} className="mr-1.5" /> {pending.length} pending review
                        </Button>
                    )}
                    {status.connected ? (
                        <>
                            <Button
                                size="sm"
                                onClick={sync}
                                disabled={syncing}
                                className="bg-stone-900 hover:bg-stone-800 text-white"
                                data-testid="gmail-sync-btn"
                            >
                                {syncing ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Syncing…</> : <><Zap size={13} className="mr-1.5" /> Sync last 45 days</>}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={disconnect} data-testid="gmail-disconnect-btn">Disconnect</Button>
                        </>
                    ) : (
                        <Button size="sm" onClick={connect} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="gmail-connect-btn">
                            <Mail size={13} className="mr-1.5" /> Connect Gmail
                        </Button>
                    )}
                </div>
            </div>

            {lastResult && (
                <div className="text-[11px] text-stone-500 -mt-2 mb-4" data-testid="gmail-last-result">
                    Last sync: scanned {lastResult.cards_scanned} active card{lastResult.cards_scanned === 1 ? "" : "s"} · matched{" "}
                    {lastResult.messages_matched} message(s) · {lastResult.pdfs_found} PDF(s) found · {lastResult.new_pending_statements} new for review.
                </div>
            )}

            <PendingReviewDialog
                open={pendingOpen}
                onOpenChange={setPendingOpen}
                items={pending}
                onRefresh={() => { loadStatus(); onSynced?.(); }}
            />
        </>
    );
}

function PendingReviewDialog({ open, onOpenChange, items, onRefresh }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Gmail sync · Pending review</DialogTitle>
                </DialogHeader>
                {items.length === 0 ? (
                    <div className="py-10 text-center text-stone-500 text-sm">
                        Nothing waiting for review. Hit &ldquo;Sync last 45 days&rdquo; to pull fresh statements.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((it) => (
                            <PendingRow key={it.id} item={it} onDone={onRefresh} />
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function PendingRow({ item, onDone }) {
    const [month, setMonth] = useState(item.guessed_month || new Date().toISOString().slice(0, 7));
    const [busy, setBusy] = useState(false);
    const [isRevised, setIsRevised] = useState(false);

    const accept = async () => {
        setBusy(true);
        try {
            await api.post(`/gmail/pending/${item.id}/accept`, {
                statement_month: month,
                run_ocr: true,
                is_revised: isRevised,
            });
            toast.success("Statement saved · OCR running.");
            onDone?.();
        } catch (err) {
            const msg = formatApiError(err);
            // Duplicate-month error → offer revised toggle
            if (msg.includes("already exists") && !isRevised) {
                if (window.confirm(msg + "\n\nUpload as revised statement?")) {
                    setIsRevised(true);
                    setBusy(false);
                    return;
                }
            }
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        if (!window.confirm("Reject and discard this fetched PDF?")) return;
        setBusy(true);
        try {
            await api.post(`/gmail/pending/${item.id}/reject`);
            toast.success("Discarded");
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border border-stone-200 rounded-xl p-4" data-testid={`gmail-pending-${item.id}`}>
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-stone-900 text-white flex items-center justify-center shrink-0">
                    <FileText size={15} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900">
                        {item.bank_name} · •••• {item.last_4} <span className="text-stone-500 font-normal">· {item.card_name || ""}</span>
                    </div>
                    <div className="text-xs text-stone-600 mt-1 truncate">
                        <span className="text-stone-500">Subject:</span> {item.subject || "—"}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                        From {item.sender || "—"} · {item.received_date && fmtDate(item.received_date)} · {Math.round((item.size_bytes || 0) / 1024)} KB · {item.attachment_filename}
                    </div>
                </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-[minmax(0,180px)_1fr_auto] gap-3 items-end">
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-stone-500">Statement month</label>
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1" data-testid={`gmail-pending-month-${item.id}`} />
                </div>
                <label className="flex items-center gap-2 text-xs text-stone-600">
                    <input type="checkbox" checked={isRevised} onChange={(e) => setIsRevised(e.target.checked)} />
                    Mark as revised statement (overrides duplicate check)
                </label>
                <div className="flex items-center gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={reject} disabled={busy} data-testid={`gmail-pending-reject-${item.id}`}>
                        <X size={13} className="mr-1.5" /> Reject
                    </Button>
                    <Button size="sm" onClick={accept} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`gmail-pending-accept-${item.id}`}>
                        {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={13} className="mr-1.5" />}
                        Accept & OCR
                    </Button>
                </div>
            </div>
        </div>
    );
}

// End of file
